import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight } from "lucide-react";

const ROUTES = [
  { to: "/app/dashboard", label: "Tableau de bord exécutif", section: "Pilotage", kw: "kpi accueil home dashboard" },
  { to: "/app/ai-agents", label: "Agents IA spécialisés", section: "Pilotage", kw: "ai claude assistant" },
  { to: "/app/treasury", label: "Trésorerie", section: "Finance", kw: "treso cash banque" },
  { to: "/app/accounting", label: "Comptabilité", section: "Finance", kw: "compta ohada journal" },
  { to: "/app/invoices", label: "Facturation", section: "Finance", kw: "facture vat tva" },
  { to: "/app/suppliers", label: "Fournisseurs", section: "Opérations", kw: "supplier achat" },
  { to: "/app/clients", label: "Clients", section: "Opérations", kw: "customer crm" },
  { to: "/app/purchases", label: "Achats (BC)", section: "Opérations", kw: "purchase achat commande" },
  { to: "/app/stock", label: "Stock", section: "Opérations", kw: "stock inventaire articles" },
  { to: "/app/vehicles", label: "Parc automobile", section: "Flotte", kw: "voiture vehicule" },
  { to: "/app/maintenance", label: "Maintenance véhicules", section: "Flotte", kw: "garage vidange" },
  { to: "/app/fuel", label: "Carburant", section: "Flotte", kw: "essence gasoil station" },
  { to: "/app/pos", label: "Caisse / POS", section: "Métiers", kw: "vente ticket caisse restaurant bar" },
  { to: "/app/chantiers", label: "Chantiers", section: "Métiers", kw: "btp projet chantier" },
  { to: "/app/hr", label: "Ressources Humaines", section: "RH", kw: "rh salaire employes contrat" },
  { to: "/app/epi", label: "Dotations EPI", section: "RH", kw: "epi protection casque" },
  { to: "/app/hse", label: "HSE", section: "Gouvernance", kw: "hse incident securite" },
  { to: "/app/legal", label: "Juridique", section: "Gouvernance", kw: "contrat bail assurance" },
  { to: "/app/mail", label: "Courrier", section: "Gouvernance", kw: "courrier mail" },
  { to: "/app/workflows", label: "Workflows", section: "Gouvernance", kw: "approbation validation" },
  { to: "/app/users", label: "Utilisateurs", section: "Admin", kw: "users user role" },
  { to: "/app/audit", label: "Journal d'audit", section: "Admin", kw: "audit log securite" },
  { to: "/app/backup", label: "Sauvegardes", section: "Admin", kw: "export import migration" },
  { to: "/app/settings", label: "Paramètres", section: "Admin", kw: "params reglages config" },
];

export default function CommandPalette({ open, onClose, onOpenAI }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => { if (open) setQ(""); }, [open]);

  const filtered = ROUTES.filter(
    (r) => !q || r.label.toLowerCase().includes(q.toLowerCase()) || r.kw.includes(q.toLowerCase()) || r.section.toLowerCase().includes(q.toLowerCase())
  );

  const go = (to) => { navigate(to); onClose(); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}
            className="fixed inset-0 bg-obsidian-950/70 backdrop-blur-md z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="fixed top-[12%] left-1/2 -translate-x-1/2 w-[min(640px,92vw)] bg-obsidian-900 border border-line rounded-sm shadow-2xl z-50 overflow-hidden"
            data-testid="command-palette"
          >
            <div className="flex items-center gap-3 px-4 h-12 border-b border-line">
              <Search size={15} className="text-obsidian-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") onClose();
                  if (e.key === "Enter" && filtered[0]) go(filtered[0].to);
                }}
                placeholder="Rechercher pages, modules, actions…"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-obsidian-500"
                data-testid="cmdk-input"
              />
              <kbd className="text-[10px] font-mono text-obsidian-500 px-1.5 py-0.5 bg-obsidian-800 rounded">esc</kbd>
            </div>
            <div className="max-h-[60vh] overflow-y-auto py-2">
              <button
                onClick={() => { onClose(); onOpenAI(); }}
                className="w-full flex items-center gap-3 px-4 h-11 hover:bg-obsidian-800 transition-colors"
                data-testid="cmdk-open-ai"
              >
                <div className="w-7 h-7 grid place-items-center bg-citron rounded-sm font-mono text-xs font-bold text-obsidian-950">AI</div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold">Assistant IA</div>
                  <div className="text-[10px] text-obsidian-400">Claude Sonnet 4.5 · Copilote exécutif</div>
                </div>
                <ArrowRight size={14} className="text-obsidian-500" />
              </button>
              {filtered.map((r, i) => (
                <button
                  key={r.to}
                  onClick={() => go(r.to)}
                  className="w-full flex items-center gap-3 px-4 h-10 hover:bg-obsidian-800 transition-colors text-left"
                  data-testid={`cmdk-result-${i}`}
                >
                  <span className="text-[10px] font-mono uppercase text-obsidian-500 w-20 truncate">{r.section}</span>
                  <span className="text-sm flex-1">{r.label}</span>
                  <span className="text-[10px] font-mono text-obsidian-500">{r.to}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-obsidian-500">Aucun résultat</div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
